import { map as _map, find as _find, isEmpty as _isEmpty } from "lodash-es";
import React, { useCallback, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  CalendarIcon,
  ChatAltIcon,
  TrashIcon,
  PencilIcon,
} from "@heroicons/react/solid";
import styled from "styled-components";

import NoteInput from "./NoteInput";
import Tooltip from "../../components/Tooltip";
import {
  updateHumanComment,
  deleteHumanComment,
} from "../../actions/humanComments";
import { deleteEvent, updateEvent } from "../../actions/events";
import { getSelectedHumanComments } from "../../selectors/humanComments";
import { autoresizeTextarea, focusOnRender, useRandomId } from "../../utils";
import { getHumanEvents } from "../../selectors/index";
import dayjs from "../../utils/dayjs";

import Markup from "../../components/Markup";
import Button from "../../components/Button";

import HumanIcon from "../../assets/human-icon.png";
import { EventDateInput } from "./NewCommentForm";

const EditNoteForm = ({ value, isEvent, date, onDone }) => {
  const formId = useRandomId("EditNoteForm");

  const [newDate, setNewDate] = useState(date);

  const [text, setText] = useState(value);
  const textareaRef = useRef();

  const setTextareaRef = useCallback((el) => {
    textareaRef.current = el;
    autoresizeTextarea(textareaRef);
    focusOnRender(el);
  }, []);

  const handleChange = useCallback((e) => {
    setText(e.target.value);
    autoresizeTextarea(textareaRef);
  }, []);

  const onSave = useCallback(
    (event) => {
      event.preventDefault();
      if (event && event.relatedTarget) {
        let t = event.relatedTarget;

        while (t) {
          if (t.id === formId || t?.className?.includes("rta__autocomplete")) {
            return; // ignore onblur if clicking in user tag autocomplete window
          }
          t = t.parentElement;
        }
      }

      onDone({ value: text, date: newDate });
    },
    [text, onDone, formId, newDate]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (e.keyCode === 27 || (e.ctrlKey && e.keyCode === 13)) {
        // Esc || Ctrl + Enter
        onSave(e);
      }
    },
    [onSave]
  );
  return (
    <form id={formId} onSubmit={onSave} onKeyDown={onKeyDown} onBlur={onSave}>
      {isEvent && (
        <div className="block mb-1">
          <EventDateInput
            type="date"
            name="date"
            value={newDate}
            onChange={(e) => {
              setNewDate(e.target.value);
            }}
          />
        </div>
      )}
      <NoteInput
        actiontype="edit"
        rows={4}
        innerRef={setTextareaRef}
        autoFocus
        value={text}
        onChange={handleChange}
        tabIndex="1"
      />
      <Button
        data-testid="edit-commentorevent"
        className="mt-2"
        onClick={onSave}
        tabIndex="2"
        disabled={!text}
      >
        Save
      </Button>
    </form>
  );
};

EditNoteForm.propTypes = {
  value: PropTypes.string.isRequired,
  isEvent: PropTypes.bool,
  date: PropTypes.string,
  onDone: PropTypes.func.isRequired,
};

const EditCommentForm = ({ commentId, onDone }) => {
  const dispatch = useDispatch();

  const comment = useSelector((state) =>
    _find(state.humanComments, { id: commentId })
  );

  const onSave = useCallback(
    ({ value: newText }) => {
      if (comment.text !== newText) {
        dispatch(
          updateHumanComment(comment.id, { text: newText, modified: true })
        );
      }
      onDone();
    },
    [comment, onDone, dispatch]
  );

  return <EditNoteForm value={comment.text} onDone={onSave} />;
};

EditCommentForm.propTypes = {
  commentId: PropTypes.string.isRequired,
  onDone: PropTypes.func.isRequired,
};

const EditEventForm = ({ eventId, onDone }) => {
  const dispatch = useDispatch();

  const event = useSelector((state) => _find(state.events, { id: eventId }));

  const onSave = useCallback(
    ({ value: newLabel, date: newDate }) => {
      if (event.label !== newLabel || event.date !== newDate) {
        dispatch(
          updateEvent(event.id, {
            label: newLabel,
            date: newDate,
            modified: true,
          })
        );
      }
      onDone();
    },
    [event, onDone, dispatch]
  );

  return (
    <EditNoteForm
      isEvent
      date={event.date}
      value={event.label}
      onDone={onSave}
    />
  );
};

EditEventForm.propTypes = {
  eventId: PropTypes.string.isRequired,
  onDone: PropTypes.func.isRequired,
};

const TimelineItemEl = styled.div`
  :not(:hover) .timeline-btn svg {
    // text-gray-100
    color: rgba(243, 244, 246, 1) !important;
  }
`;

const TimelineItem = ({ type, actions, children, date }) => {
  const user = useSelector((state) => state.user);

  return (
    <TimelineItemEl className="flex items-start space-x-3 pb-8">
      <div className="relative">
        <img
          className="h-10 w-10 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white border"
          src={user.photo ? user.photo : HumanIcon}
          alt=""
        />

        <span className="absolute -bottom-0.5 -right-1 bg-white rounded-tl px-0.5 py-px">
          {type === "comment" ? (
            <ChatAltIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          ) : (
            <CalendarIcon
              className="h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
          )}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div>
          <div className="text-sm flex justify-between">
            <Tooltip
              className="w-max"
              placement="top"
              tooltipChildren={dayjs(date).format("MMMM Do, YYYY")}
            >
              <div className="text-sm text-gray-500 cursor-default">
                {dayjs(date).fromNow()}
              </div>
            </Tooltip>
            <div className="flex">
              {actions.map((action, key) => {
                return (
                  <div
                    className="mr-1 timeline-btn"
                    key={key}
                    onClick={action.onClick}
                  >
                    {action.icon}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="mt-2 text-sm text-gray-700 break-words">{children}</div>
      </div>
    </TimelineItemEl>
  );
};

TimelineItem.propTypes = {
  type: PropTypes.string.isRequired,
  actions: PropTypes.any.isRequired,
  date: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired,
};

const HumanComment = ({ comment }) => {
  const dispatch = useDispatch();

  const [editing, setEditing] = useState(false);

  const onEditingStart = useCallback(() => {
    setEditing(true);
  }, []);
  const onEditingDone = useCallback(() => {
    setEditing(false);
  }, []);

  const onDelete = useCallback(() => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      dispatch(deleteHumanComment(comment.id));
    }
  }, [comment, dispatch]);

  const ACTIONS = [
    {
      icon: (
        <PencilIcon
          data-testid={`edit-comment-icon-${comment.id}`}
          className="h-5 w-5 text-gray-600 hover:text-gray-400 cursor-pointer"
        />
      ),
      onClick: onEditingStart,
    },
    {
      icon: (
        <TrashIcon className="h-5 w-5 text-red-600 cursor-pointer hover:text-red-400" />
      ),
      onClick: onDelete,
    },
  ];
  return (
    <TimelineItem actions={ACTIONS} type="comment" date={comment.createdAt}>
      <>
        {editing ? (
          <EditCommentForm commentId={comment.id} onDone={onEditingDone} />
        ) : (
          <div data-testid={`comment-container-${comment.id}`}>
            {comment.text.split("\n").map((text, key) => (
              <div key={key}>
                {text ? (
                  <>
                    <Markup>{text}</Markup>
                  </>
                ) : (
                  <>&nbsp;</>
                )}
              </div>
            ))}
          </div>
        )}
      </>
    </TimelineItem>
  );
};

HumanComment.propTypes = {
  comment: PropTypes.object.isRequired,
};

const HumanEvent = ({ event }) => {
  const dispatch = useDispatch();

  const [editing, setEditing] = useState(false);

  const onEditingStart = useCallback(() => {
    setEditing(true);
  }, []);
  const onEditingDone = useCallback(() => {
    setEditing(false);
  }, []);

  const onDelete = useCallback(() => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      dispatch(deleteEvent(event.id));
    }
  }, [event, dispatch]);

  const ACTIONS = [
    {
      icon: (
        <PencilIcon
          data-testid={`edit-event-${event.id}`}
          className="h-5 w-5 text-gray-600 hover:text-gray-400 cursor-pointer"
        />
      ),
      onClick: onEditingStart,
    },
    {
      icon: (
        <TrashIcon className="h-5 w-5 text-red-600 cursor-pointer hover:text-red-400r" />
      ),
      onClick: onDelete,
    },
  ];

  return (
    <TimelineItem actions={ACTIONS} type="event" date={event.date}>
      <>
        {editing ? (
          <EditEventForm eventId={event.id} onDone={onEditingDone} />
        ) : (
          <div data-testid={`event-container-${event.id}`}>
            {event.label.split("\n").map((label, key) => (
              <p key={key}>
                <Markup>{label}</Markup>
              </p>
            ))}
          </div>
        )}
      </>
    </TimelineItem>
  );
};

HumanEvent.propTypes = {
  event: PropTypes.object.isRequired,
};

const getObjSortDateField = (obj) => {
  if (obj._type === "human") return obj.createdAt;
  if (obj._type === "event") return obj.date;
};

const TimelineList = styled.li`
  &:last-child {
    .vertical-border {
      display: none;
    }
  }
`;
const HumanComments = (props) => {
  const comments = useSelector((state) =>
    getSelectedHumanComments(state, props)
  );
  const events = useSelector((state) => getHumanEvents(state, props));

  const commentsAndEvents = useMemo(() => {
    const allObjects = [
      ..._map(comments, (c) => ({ ...c, _type: "human" })),
      ..._map(events, (e) => ({ ...e, _type: "event" })),
    ];

    allObjects.sort((a, b) =>
      dayjs.utc(getObjSortDateField(b)).diff(dayjs.utc(getObjSortDateField(a)))
    );

    return allObjects;
  }, [comments, events]);

  if (_isEmpty(commentsAndEvents)) {
    return "";
  }
  return (
    <div className="flow-root mt-5 text-left bg-white p-4 rounded-lg shadow">
      <ul data-testid="comment-event-wrapper" className="-mb-8 pb-16 lg:pb-0">
        {_map(commentsAndEvents, (c, index) => {
          return (
            <TimelineList key={index}>
              <div className="relative">
                <span
                  className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200 vertical-border"
                  aria-hidden="true"
                />
                {c._type === "human" ? (
                  <HumanComment comment={c} />
                ) : (
                  <HumanEvent event={c} />
                )}
              </div>
            </TimelineList>
          );
        })}
      </ul>
    </div>
  );
};

export default HumanComments;
